import {Title} from '../../entity/model/title';
import {Items} from '../../entity/resource/items';
import {Section} from '../../entity/resource/section';
import {UniqueIdentifier} from '@dnd-kit/core';
import {RequestBody} from '../../entity/core/request-body';
import SectionStore from '../store/section-store';
import {MovingType} from '../../entity/enums/moving-type';
import {DropdownOption} from '../../entity/core/dropdown-option';
import {toast} from 'react-toastify';
import {TitleTemplate} from '../../entity/model/title-template';
import {AddSectionDto} from '../../entity/dto/add-section-dto';

export default class SectionService {
  sectionStore: SectionStore;

  constructor(sectionStore: SectionStore) {
    this.sectionStore = sectionStore;
  }

  moveSection = async (organizationId: string, sectionIds: string[] | UniqueIdentifier[], titleId?: string, templateId?: string): Promise<Title | TitleTemplate | undefined> => {
    const requestBody: RequestBody = {body: {sectionsOrder: sectionIds}};

    const response = await this.sectionStore.moveSection(organizationId, requestBody, titleId, templateId);

    if (typeof response === 'string')
      toast.error(response);
    else
      return response;
  };

  deleteSection = async (
    organizationId: string,
    sectionId: UniqueIdentifier,
    titleId?: string,
    templateId?: string
  ): Promise<boolean> => {
    const response = await this.sectionStore.deleteSection(organizationId, sectionId, titleId, templateId);
    if (response) {
      toast.error(response);
      return false;
    } else toast.success('Section deleted successfully !!');

    return true;
  };

  findSectionById = (sections: Section[], id: UniqueIdentifier): Section | undefined => {
    return sections.find((section: Section) => section.id === id);
  };

  getSectionOptions = (sections: Section[]): DropdownOption[] => {
    const sectionOptions: DropdownOption[] = [
      {
        key: 'Invoices',
        text: 'Invoices',
        value: 'Invoices'
      },
      {
        key: 'Receipts',
        text: 'Receipts',
        value: 'Receipts'
      },
      {
        key: 'Title Summary',
        text: 'Title Summary',
        value: 'Title Summary'
      },
      {
        key: 'Chain Sheet',
        text: 'Chain Sheet',
        value: 'Chain Sheet'
      },
      {
        key: 'Property Card(s)',
        text: 'Property Card(s)',
        value: 'Property Card(s)'
      },
      {
        key: 'Tax Data',
        text: 'Tax Data',
        value: 'Tax Data'
      },
      {
        key: 'Mobile Home Data',
        text: 'Mobile Home Data',
        value: 'Mobile Home Data'
      },
      {
        key: 'Mortgage(s)',
        text: 'Mortgage(s)',
        value: 'Mortgage(s)'
      },
      {
        key: 'Deeds',
        text: 'Deeds',
        value: 'Deeds'
      },
      {
        key: 'Covenants',
        text: 'Covenants',
        value: 'Covenants'
      },
      {
        key: 'Easements & Right of Ways',
        text: 'Easements & Right of Ways',
        value: 'Easements & Right of Ways'
      },
      {
        key: 'Leases & Lease Assignments',
        text: 'Leases & Lease Assignments',
        value: 'Leases & Lease Assignments'
      },
      {
        key: 'Plats',
        text: 'Plats',
        value: 'Plats'
      },
      {
        key: 'Liens',
        text: 'Liens',
        value: 'Liens'
      },
      {
        key: 'Judgments',
        text: 'Judgments',
        value: 'Judgments'
      },
      {
        key: 'Estates',
        text: 'Estates',
        value: 'Estates'
      },
      {
        key: 'Power of Attorney',
        text: 'Power of Attorney',
        value: 'Power of Attorney'
      },
      {
        key: 'UCC Filings',
        text: 'UCC Filings',
        value: 'UCC Filings'
      },
      {
        key: 'Miscellaneous',
        text: 'Miscellaneous',
        value: 'Miscellaneous'
      },
      {
        key: 'Index / Check Sheets',
        text: 'Index / Check Sheets',
        value: 'Index / Check Sheets'
      },
      {
        key: 'Pre-Closing Update',
        text: 'Pre-Closing Update',
        value: 'Pre-Closing Update'
      },
      {
        key: 'Post-Closing Update',
        text: 'Post-Closing Update',
        value: 'Post-Closing Update'
      },
      {
        key: 'Gap Update',
        text: 'Gap Update',
        value: 'Gap Update'
      },
      {
        key: 'Orders',
        text: 'Orders',
        value: 'Orders'
      },
      {
        key: 'Vesting Deed(s)',
        text: 'Vesting Deed(s)',
        value: 'Vesting Deed(s)'
      },
      {
        key: 'Back Deed(s)',
        text: 'Back Deed(s)',
        value: 'Back Deed(s)'
      },
      {
        key: 'GIS Map(s)',
        text: 'GIS Map(s)',
        value: 'GIS Map(s)'
      }
    ];

    const sectionNames = sections.map((section) => section.name);
    return sectionOptions.filter((option) => !sectionNames.includes(option.value));
  };

  getItems = (sections: Section[]): Items => {
    return sections.reduce((acc: any, section: Section) => {
      acc[section.id] = section?.documents?.map((doc) => doc.id);
      return acc;
    }, {});
  };

  getSelectedIds = (): UniqueIdentifier[] => {
    return this.sectionStore.selectedIds;
  };

  setSelectedIds = (selectedIds: UniqueIdentifier[]): void => {
    this.sectionStore.selectedIds = selectedIds;
  };

  getSectionMoving = (): boolean => {
    return this.sectionStore.isSectionMoving;
  };

  setSectionMoving = (status: boolean): void => {
    this.sectionStore.isSectionMoving = status;
  };

  getMovingSectionId = (): UniqueIdentifier => {
    return this.sectionStore.movingSectionId;
  };

  setMovingSectionId = (sectionId: UniqueIdentifier | string): void => {
    this.sectionStore.movingSectionId = sectionId;
  };

  getMovingType = (sections: Section[], id: UniqueIdentifier): MovingType => {
    const sectionExists = sections.some((section) => section.id === id);
    if (sectionExists) return MovingType.SECTION;

    const documentExists = sections.some((section) => section?.documents?.some((document) => document.id === id));
    if (documentExists) return MovingType.DOCUMENT;

    return MovingType.NOT_FOUND;
  };

  setMovableItems = (items: Items): void => {
    this.sectionStore.movableItems = items;
  };

  getExpandAllContainers = (): boolean => {
    return this.sectionStore.expandAllContainers;
  };

  setExpandSingleContainer = (status: boolean): void => {
    this.sectionStore.expandSingleContainer = status;
  };

  addToExpandedSectionIds = (id: UniqueIdentifier | null): void => {
    if (!id) this.sectionStore.expandedSectionIds = [];
    else this.sectionStore.expandedSectionIds = [...this.sectionStore.expandedSectionIds, id];
  };

  removeExpandedSectionIds = (id: UniqueIdentifier | null): void => {
    this.sectionStore.expandedSectionIds = this.sectionStore.expandedSectionIds.filter((sectionId) => sectionId !== id);
  };

  clearExpandedSectionIds = (): void => {
    this.sectionStore.expandedSectionIds = [];
  };

  getExpandSectionIds = (): UniqueIdentifier[] => {
    return this.sectionStore.expandedSectionIds;
  };

  filterSections = (
    sections: UniqueIdentifier[],
    activeSection: UniqueIdentifier,
    selectedIds: UniqueIdentifier[]
  ): UniqueIdentifier[] => {
    if (!activeSection) return sections;

    return sections.filter((id) => id === activeSection || !selectedIds.includes(id));
  };

  getViewDocument = (): boolean => {
    return this.sectionStore.viewDocument;
  };

  setViewDocument = (status: boolean): void => {
    this.sectionStore.viewDocument = status;
  };

  addNewSection = async (organizationId: string, templateId: string, sections: string[]): Promise<TitleTemplate | undefined> => {
    if (sections.length < 1) {
      toast.warning('Please select at least one section !!');
      return;
    }

    const body = sections.map((section: string) => ({ name: section }));
    const response = await this.sectionStore.addNewSection(organizationId, templateId, {body});

    if (typeof response == 'string') {
      toast.error(response);
      return;
    } else {
      toast.success('New section added successfully !!');
      return response;
    }
  };

  getAddNewSectionDto = (): AddSectionDto => {
    return {
      sections: []
    };
  }
}
